import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { SeatMapPermissions, SeatMapRestrictions } from '@websky/graphql';
import { RowContext } from '../SeatMap/Row/context';
import { useIataCode } from '../../../hooks';
import { isExistI18nTranslate } from '../../../utils';
import { Seat } from '../../types';

export const useRestrictionsConditionForKV = () => {
	const row = useContext(RowContext)?.row;
	const iataCode = useIataCode();

	return iataCode === 'KV' && row ? row.exitRow : true;
};

export const useSeatAdditionalInfo = (seats: Seat[]) => {
	const { t } = useTranslation('SeatMap');

	const mainSeat = seats?.[0];
	const combinedSeat = mainSeat?.combinedWith ? seats.find(seat => seat.combinedWith[mainSeat.number]) : null;
	const formattedSeats = combinedSeat ? [mainSeat, combinedSeat] : [mainSeat];

	const additional: string[] = [];
	let restrictions: SeatMapRestrictions[] = [];
	let permissions: SeatMapPermissions[] = [];

	const sortDescription = (first: string, second: string) => first.localeCompare(second);

	formattedSeats.forEach(seat => {
		if (!seat?.isRecline) {
			additional.push(t('Chair back does not recline'));
		}

		if (seat?.isNearWindow) {
			additional.push(t('Window seat'));
		} else if (seat?.isNearWindow === false || seat?.isNearWindowWithoutWindow) {
			additional.push(t('Seat with no window'));
		}

		if (seat?.isComfort) {
			additional.push(t('Comfort seat'));
		}

		if (seat?.isNearPartitionWall) {
			additional.push(t('Seat next to the partition'));
		}

		if (seat?.isMoreSpaceForLegs) {
			additional.push(t('Seat with increased legroom'));
		}

		restrictions = [...restrictions, ...(seat?.restrictions ?? [])];
		permissions = [...permissions, ...(seat?.permissions ?? [])];

		const attributes =
			seat?.attributes
				?.filter(
					attribute =>
						attribute.value === '1' &&
						isExistI18nTranslate(`Attribute_${attribute.name}`, { ns: 'SeatMap' })
				)
				.map(attribute => t(`Attribute_${attribute.name}`)) ?? [];

		if (attributes.length) {
			additional.push(...attributes);
		}
	});

	const uniqueAdditional = [...new Set(additional)].sort(sortDescription);
	const uniqueRestrictions = [...new Set(restrictions)].sort(sortDescription);
	const uniquePermissions = [...new Set(permissions)].sort(sortDescription);

	const withInfo =
		uniqueAdditional.length > 0 ||
		(uniqueRestrictions.length > 0 && useRestrictionsConditionForKV()) ||
		uniquePermissions.length > 0;

	return {
		restrictions: uniqueRestrictions,
		permissions: uniquePermissions,
		additional: uniqueAdditional,
		withInfo
	};
};
